import { Box, Button, Grid, TextField, Tooltip, Typography, Stack } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getAlertByAlertID, postAlert, putAlertById } from '../requests';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { alertResponseType, areaCoordinateType } from '../types';
import AlertDetailSetter from './AlertDetailSetter';
import 'ol/ol.css';
import RectangularSelectMap from './RectangularSelectMap';
import useAddressInput from '../../../hooks/useAddressInput';
import { useOtherSettings } from './useOtherSettings';
import { userModelType } from '../../../features/auth/types';
import { store } from '../../../store';
import { authType } from '../../../features/client/types';
import useFetchUser from '../../../features/client/hooks/useFetchUser';
import usePaymentModal from '../../../features/predictionMap/hooks/usePaymentModal';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

const styles = {
  textField: {
    width: '100%',
    height: 30,
    padding: '0 16px',
  },
};

const AreaSelectTab: FC = () => {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const authInfo = useLoaderData() as authType;
  const { user, isPaid } = useFetchUser(authInfo.user.cognito_username);
  const { togglePaymentModal, PaymentModal } = usePaymentModal('alert');
  const [areaName, setAreaName] = useState<string>('');
  const [shouldRevGeoCode, setShouleRevGeoCode] = useState<boolean>(false);
  const [areaCoordinates, setAreaCoordinates] = useState<areaCoordinateType | null>(null);
  const [areaCenterAddress, setAreaCenterAddress] = useState<string>('');
  const { OtherSettingsComponents, otherSettings, setOtherSettings } = useOtherSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const initialLatlng = useRef<null | [number, number]>(null);
  const { latlng: latLonByAddressInput, AddressInput } = useAddressInput({
    placeholder: t('pages.alerts.addressSample'),
  });

  const is_detail = new URLSearchParams(location.search).get('is_detail');

  // リバースジオコードのためのHook
  useEffect(() => {
    if (!areaCoordinates || !shouldRevGeoCode) {
      return;
    }
    const ac = areaCoordinates;
    const areaCenterLat = (ac.topLeftLat + ac.bottomRightLat) / 2;
    const areaCenterLon = (ac.topLeftLon + ac.bottomRightLon) / 2;
    const latlng = { lat: areaCenterLat, lng: areaCenterLon };
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latlng, language: language }).then(res => {
      const result = res.results[0].address_components;
      if (result.length < 3) {
        alert('住所を特定できませんでした.'); // FIXME: 開発者用
        return;
      }
      const resultString = crtAddress(result);
      setAreaCenterAddress(resultString);
    });
  }, [areaCoordinates, language]);

  // only called when first rendering
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');

    if (!!id) {
      getAlertByAlertID(id)
        .then(data => {
          if (!data) {
            throw new Error('');
          }
          setAreaCoordinates({
            topLeftLat: data?.latitude_nw || data.latitude,
            topLeftLon: data?.longitude_nw || data.longitude,
            bottomRightLat: data?.latitude_se || data.latitude,
            bottomRightLon: data?.longitude_se || data.longitude,
          });
          setAreaName(data.name);
          setAreaCenterAddress(data?.address || '');
          initialLatlng.current = [
            data?.latitude_nw || data.latitude,
            data?.longitude_nw || data.longitude,
          ];
        })
        .catch(d => {
        });
    }
  }, [location]);

  useEffect(() => {
    if (!isPaid) {
      togglePaymentModal();
    }
  }, [areaCenterAddress]);

  const handleNextClick = async () => {
    if (!areaCoordinates) {
      alert(t('pages.alerts.invalidLatLon'));
      return;
    }
    const user = (await store.getItem('user')) as userModelType;
    const data = {
      name: areaName,
      address: areaCenterAddress,
      latitude: areaCoordinates.topLeftLat,
      longitude: areaCoordinates.topLeftLon,
      is_area: true,
      latitude_nw: areaCoordinates.topLeftLat,
      longitude_nw: areaCoordinates.topLeftLon,
      latitude_se: areaCoordinates.bottomRightLat,
      longitude_se: areaCoordinates.bottomRightLon,
      original_user: (!otherSettings.shareWithClient && user.id) || null,
    };
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');
    let res = false as alertResponseType | boolean;
    // idがあったらput
    if (!!id) {
      res = await putAlertById(id, data);
    } else {
      res = await postAlert(data);
    }
    if (!!res && !!res) {
      // GA4: エリアアラートの登録・更新成功を計測
      sendAnalyticsEvent('alert_form', {
        action: !!id ? 'update_success' : 'create_success',
        alert_type: 'area',
      });
      navigate(`/prediction/new_alert?id=${res.id}&is_detail=true`);
    }
    return;
  };

  const canSend = !!areaName && !!areaCoordinates && isPaid;

  if (is_detail) {
    return (
      <>
        <AlertDetailSetter />
      </>
    );
  }

  return (
    <Stack direction={isMobile ? 'column' : 'row'}>
      {PaymentModal}

      {/* 左側パネル */}
      <Box sx={{ width: isMobile ? '100%' : '45%' }}>
        <Box>
          <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
            1. {t('pages.alerts.addNewAlert.enterAddress')}
            <Tooltip title={t('pages.alerts.tooltip.addressInput')}>
              <HelpOutlineIcon style={{ cursor: 'pointer', marginLeft: 2, fontSize: '14px' }} />
            </Tooltip>
          </Typography>
          <Box style={{ textAlign: 'left', marginTop: '4px' }}>{AddressInput}</Box>
        </Box>

        <Box style={{ marginTop: '40px' }}>
          <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
            2. {t('pages.alerts.selectArea')}
          </Typography>
          <Box style={{ marginTop: '8px', marginLeft: '8px' }}>
            <Typography style={{ fontSize: '10pt' }}>{t('pages.alerts.centerAddress')}</Typography>
            <Box style={{ textAlign: 'left', marginTop: '4px' }}>
              <TextField
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: '#fff',
                  },
                }}
                inputProps={{ style: styles.textField }}
                placeholder={t('pages.alerts.areaNameSample')}
                value={areaCenterAddress}
                onChange={e => {
                  setAreaCenterAddress(e.target.value);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box style={{ margin: '12px 0px 0px 8px' }}>
          <Typography
            style={{
              fontWeight: 'bold',
              fontSize: '10pt',
            }}
          >
            {t(`pages.alerts.area.topLeftLatLon`)}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography
                style={{
                  fontSize: '10pt',
                  color: 'gray',
                }}
              >
                {t(`pages.alerts.latitude`)}: {areaCoordinates?.topLeftLat.toFixed(4)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                style={{
                  fontSize: '10pt',
                  color: 'gray',
                }}
              >
                {t(`pages.alerts.longitude`)}: {areaCoordinates?.topLeftLon.toFixed(4)}
              </Typography>
            </Grid>
          </Grid>

          <Typography
            style={{
              fontWeight: 'bold',
              fontSize: '10pt',
              marginTop: '12px',
            }}
          >
            {t(`pages.alerts.area.bottomRightLatLon`)}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography
                style={{
                  fontSize: '10pt',
                  color: 'gray',
                }}
              >
                {t(`pages.alerts.latitude`)}: {areaCoordinates?.bottomRightLat.toFixed(4)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                style={{
                  fontSize: '10pt',
                  color: 'gray',
                }}
              >
                {t(`pages.alerts.longitude`)}: {areaCoordinates?.bottomRightLon.toFixed(4)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box style={{ marginTop: '32px' }}>
          <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
            3. {t('pages.alerts.addNewAlert.editAreaName')}
            <Tooltip title={t('pages.alerts.tooltip.areaName')}>
              <HelpOutlineIcon style={{ cursor: 'pointer', marginLeft: 2, fontSize: '14px' }} />
            </Tooltip>
          </Typography>
          <TextField
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: '#fff',
              },
            }}
            inputProps={{ style: styles.textField }}
            placeholder={t('pages.alerts.areaNameSample')}
            value={areaName}
            onChange={e => {
              setAreaName(e.target.value);
            }}
          />
        </Box>
        <OtherSettingsComponents
          otherSettings={otherSettings}
          setOtherSettings={setOtherSettings}
        />

        {/* 次へボタン */}
        <Box style={{ marginTop: '36px' }}>
          <Button
            variant="contained"
            style={canSend ? { backgroundColor: '#007B43' } : {}}
            onClick={handleNextClick}
            disabled={!canSend}
          >
            {t('pages.alerts.goNext')}
          </Button>
        </Box>
      </Box>

      {/* 画面の右半分（isMobile時は上に表示） */}
      <Box
        sx={{
          width: isMobile ? '100%' : '50%',
          ...(isMobile ? {
            order: -1,
            mb: 2,
            height: 300,
            minHeight: 0,
            maxHeight: 300,
            overflow: 'hidden',
          } : { marginLeft: '5%' }),
        }}
      >
        <RectangularSelectMap
          setAreaCoordinates={setAreaCoordinates}
          latLngOfCenter={latLonByAddressInput || initialLatlng.current}
          setShouleRevGeoCode={setShouleRevGeoCode}
        />
      </Box>
    </Stack>
  );
};

export default AreaSelectTab;

function crtAddress(arr: google.maps.GeocoderAddressComponent[]): string {
  return arr.reduceRight((accumulator, current) => {
    // "c"を含む要素をスキップする
    if (
      !current.types.includes('political') ||
      current.types.includes('country') ||
      current.types.includes('sublocality')
    ) {
      return accumulator;
    }
    return accumulator + current.long_name;
  }, '');
}
